import type { WeekAxis } from '../types';

import { MAX_AXIS_TICKS, MIN_TICK_PX } from './axisTicks';
import { TRACK_MIN_WIDTH_PX } from './trackLabels';
import { positionOf } from './weekAxis';

/** How a long cycle is drawn: readable weeks, a month overview, or the whole cycle fitted. */
export type TimelineZoom = 'week' | 'month' | 'cycle';

export const TIMELINE_ZOOMS: readonly TimelineZoom[] = ['week', 'month', 'cycle'];

/**
 * A week's width at each zoom (`--week-width` in TimelineBoard.module.scss: 5.5rem and
 * 1.25rem); the whole cycle has none and fits the track.
 */
export const WEEK_WIDTH_PX: Record<TimelineZoom, number> = { week: 88, month: 20, cycle: 0 };

/**
 * Whole weeks drawn after the axis while zoomed, with their ticks and grid, so today opens
 * 1.5 weeks short of the right edge on real columns rather than blank room.
 */
export const FUTURE_WEEKS = 2;

/** The future weeks a zoom draws after the axis: none for a cycle that fills the row. */
export function futureWeeksFor(zoom: TimelineZoom | null): number {
    return zoom === null ? 0 : FUTURE_WEEKS;
}

/**
 * The share of the drawn track the axis's own weeks take, the rest being future weeks. Every
 * row position is a percentage of the axis, scaled by this in CSS (`--data-share`).
 */
export function dataShare(weekCount: number, futureWeeks: number): number {
    return weekCount / Math.max(1, weekCount + futureWeeks);
}

/** Weeks of room kept right of the date the board scrolls to, so it doesn't sit on the edge. */
const ROOM_AFTER_WEEKS = 1.5;

/** The scroller's geometry in track pixels: what is drawn, and how much of it shows. */
export interface TrackViewport {
    trackPx: number;
    /** The scroller's width less the pinned label column. */
    viewPx: number;
    weekCount: number;
    /** How far the scroller can scroll: the track, plus the room after it, less the view. */
    maxScroll: number;
}

/**
 * What the view holds on to while sizes, the zoom or the data change: the latest activity
 * near the right edge (until the user scrolls), or the date the user left in the middle.
 */
export type TimelineAnchor = { kind: 'latest' } | { kind: 'centre'; ms: number };

/**
 * Whether the cycle is long enough to zoom: its weeks at Week zoom are wider than the track
 * would be anyway. A shorter one keeps filling the row, and gets no zoom controls.
 */
export function isZoomable(weekCount: number, viewPx: number | null): boolean {
    if (viewPx === null) return false;
    return weekCount * WEEK_WIDTH_PX.week > Math.max(viewPx, TRACK_MIN_WIDTH_PX);
}

/** How many header ticks a track this wide has room for; never fewer than on the narrowest. */
export function maxTicksFor(trackPx: number): number {
    return Math.max(MAX_AXIS_TICKS, Math.floor(trackPx / MIN_TICK_PX));
}

function clampScroll(left: number, viewport: TrackViewport): number {
    return Math.min(Math.max(0, viewport.maxScroll), Math.max(0, left));
}

/** The scroll offset that puts `pct` of the track about 1.5 weeks short of the visible right edge. */
export function scrollLeftToShow(pct: number, viewport: TrackViewport): number {
    const weekPx = viewport.trackPx / Math.max(1, viewport.weekCount);
    const room = Math.min(ROOM_AFTER_WEEKS * weekPx, viewport.viewPx / 4);
    return clampScroll((pct / 100) * viewport.trackPx + room - viewport.viewPx, viewport);
}

/** Where the middle of the visible track is, as a percentage of the track. */
export function centrePct(scrollLeft: number, viewport: TrackViewport): number {
    if (viewport.trackPx <= 0) return 0;
    return ((scrollLeft + viewport.viewPx / 2) / viewport.trackPx) * 100;
}

/** The scroll offset that puts `pct` of the track in the middle of the visible track. */
export function scrollLeftToCentre(pct: number, viewport: TrackViewport): number {
    return clampScroll((pct / 100) * viewport.trackPx - viewport.viewPx / 2, viewport);
}

/**
 * The date in the middle of the visible track. Unlike `positionOf`, this is not clamped to the
 * axis: a centre in the future weeks past the axis end is that date, so a later layout can
 * scroll back to it.
 */
export function centreMs(axis: WeekAxis, scrollLeft: number, viewport: TrackViewport): number {
    const span = axis.endMs - axis.startMs;
    if (viewport.trackPx <= 0 || span <= 0) return axis.startMs;
    return axis.startMs + (centrePct(scrollLeft, viewport) / 100) * span;
}

/** Where `ms` sits on the axis, as a percentage. Past the axis end this is above 100. */
function axisPct(axis: WeekAxis, ms: number): number {
    const span = axis.endMs - axis.startMs;
    if (span <= 0) return 0;
    return ((ms - axis.startMs) / span) * 100;
}

/** The scroll offset that holds `anchor` on this axis and geometry. */
export function scrollLeftForAnchor(
    anchor: TimelineAnchor,
    axis: WeekAxis,
    viewport: TrackViewport
): number {
    return anchor.kind === 'latest'
        ? scrollLeftToShow(positionOf(axis, new Date(axis.anchorMs)), viewport)
        : scrollLeftToCentre(axisPct(axis, anchor.ms), viewport);
}
